import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MailboxListRow, MailboxMessageSummary, MailboxThread, MailboxThreadState } from '../_types/mailbox';

/// Listado de mensajes de una carpeta.
///
/// Usa `p-table` en modo perezoso con paginador, **no desplazamiento virtual**. El motivo es concreto: cada página
/// exige un viaje al servidor de correo que tarda entre dos y ocho décimas, y con desplazamiento virtual eso se
/// percibe roto (huecos que se rellenan tarde, saltos de posición). Además, cualquier mensaje nuevo desplaza los
/// índices de todo el listado y aparecerían filas duplicadas. Un paginador es honesto respecto a lo que el
/// servidor puede dar, y es la convención del resto de la aplicación.
///
/// La cabecera va oculta y cada fila ocupa dos líneas, porque el panel es estrecho: no caben columnas. Y la fila
/// no lleva botones: marcar, destacar y borrar se hacen desde la barra del mensaje abierto, que es donde se ve
/// sobre qué se está actuando.
@Component({
    selector: 'app-message-list',
    templateUrl: './message-list.component.html',
    styleUrls: ['./message-list.component.css'],
    standalone: false
})
export class MessageListComponent implements OnChanges {
  @Input() messages: MailboxMessageSummary[] = [];
  @Input() totalRecords: number = 0;
  @Input() rows: number = 50;
  @Input() first: number = 0;
  @Input() loading: boolean = false;
  @Input() selectedUid: number | null = null;

  /// Ver mensajes agrupados por conversación.
  @Input() grouped: boolean = false;

  /// Conversaciones ya completadas contra el servidor, por clave. Las trae la página, que es quien habla con el
  /// servicio; aquí solo se pintan.
  @Input() conversations: ReadonlyMap<string, MailboxThreadState> = new Map();

  @Output() pageChanged = new EventEmitter<{ first: number; rows: number }>();
  @Output() messageSelected = new EventEmitter<MailboxMessageSummary>();

  /// Se ha desplegado una conversación: la página decide si hay que pedirla al servidor.
  @Output() conversationToggled = new EventEmitter<MailboxThread>();

  /// Lo que se pinta: mensajes sueltos, o cabeceras de conversación con sus mensajes cuando están desplegadas.
  public displayRows: MailboxListRow[] = [];

  private expanded = new Set<string>();

  ngOnChanges() {
    this.rebuildRows();
  }

  onLazyLoad(event: any) {
    this.pageChanged.emit({ first: event.first ?? 0, rows: event.rows ?? this.rows });
  }

  isExpanded(key: string): boolean {
    return this.expanded.has(key);
  }

  stateOf(key: string): MailboxThreadState | null {
    return this.conversations.get(key) ?? null;
  }

  /// Si el mensaje abierto está dentro de esta conversación, para que la fila se vea seleccionada aunque esté
  /// plegada.
  threadHasSelected(thread: MailboxThread): boolean {
    return this.selectedUid != null && thread.messages.some(message => message.uid === this.selectedUid);
  }

  /// Despliega o pliega una conversación.
  ///
  /// Al desplegarla se abre además su mensaje más reciente: agrupado, la fila de la conversación es lo que se
  /// pulsa para leer, y obligar a un segundo clic para llegar al correo sería un paso de más en el caso normal,
  /// que es una conversación de un solo mensaje.
  toggleThread(thread: MailboxThread) {
    if (this.expanded.has(thread.key)) {
      this.expanded.delete(thread.key);
      this.rebuildRows();
      return;
    }

    this.expanded.add(thread.key);
    this.rebuildRows();

    this.conversationToggled.emit(thread);
    this.messageSelected.emit(thread.newest);
  }

  /// Agrupa los mensajes de la página por su clave de conversación.
  ///
  /// La agrupación es de lo que hay en la página, y por eso **toda** conversación se puede desplegar aunque solo
  /// se le conozca un mensaje: los demás pueden estar en otra página, y es al desplegarla cuando se le pregunta al
  /// servidor por el hilo completo.
  private rebuildRows() {
    if (!this.grouped) {
      this.displayRows = this.messages.map(message => ({ kind: 'message' as const, message, child: false }));
      return;
    }

    //un Map conserva el orden de inserción, que es el del listado: lo más reciente primero
    const grupos = new Map<string, MailboxMessageSummary[]>();
    for (const message of this.messages) {
      const clave = message.threadKey || `uid:${message.uid}`;
      const miembros = grupos.get(clave);
      if (miembros) miembros.push(message);
      else grupos.set(clave, [message]);
    }

    const filas: MailboxListRow[] = [];
    for (const [clave, miembros] of grupos) {
      const estado = this.conversations.get(clave);
      const completos = estado?.messages ?? null;
      const thread = MessageListComponent.buildThread(clave, completos ?? miembros, completos != null);

      filas.push({ kind: 'thread', thread });

      if (this.expanded.has(clave)) {
        for (const message of thread.messages) filas.push({ kind: 'message', message, child: true });
      }
    }

    this.displayRows = filas;
  }

  private static buildThread(
    key: string, messages: MailboxMessageSummary[], complete: boolean): MailboxThread {

    //una conversación se lee del más antiguo al más reciente
    const ordenados = [...messages].sort((a, b) => MessageListComponent.time(a.date) - MessageListComponent.time(b.date));
    const primero = ordenados[0];
    const ultimo = ordenados[ordenados.length - 1];

    return {
      key,
      //el asunto del primero, que es el que no lleva encima la pila de «RE:» que van añadiendo las respuestas
      subject: primero?.subject || ultimo?.subject || '(sin asunto)',
      participants: MessageListComponent.participantsLabel(ordenados),
      date: ultimo?.date ?? new Date(),
      count: ordenados.length,
      complete,
      unread: ordenados.some(message => !message.seen),
      flagged: ordenados.some(message => message.flagged),
      hasAttachments: ordenados.some(message => message.hasAttachments),
      answered: ordenados.some(message => message.answered),
      forwarded: ordenados.some(message => message.forwarded),
      newest: ultimo ?? primero,
      messages: ordenados
    };
  }

  /// Quiénes escriben en la conversación, sin repetir y en el orden en que aparecen. Con más de tres se corta,
  /// porque el panel es estrecho.
  private static participantsLabel(messages: MailboxMessageSummary[]): string {
    const nombres: string[] = [];

    for (const message of messages) {
      const nombre = message.from?.name?.trim() || message.from?.address || '(sin remitente)';
      if (!nombres.includes(nombre)) nombres.push(nombre);
    }

    if (nombres.length === 0) return '(sin remitente)';
    if (nombres.length <= 3) return nombres.join(', ');

    return `${nombres.slice(0, 2).join(', ')} y ${nombres.length - 2} más`;
  }

  private static time(date: Date): number {
    return (date instanceof Date ? date : new Date(date)).getTime();
  }

  /// Nombre visible del remitente, cayendo a la dirección si no trae nombre.
  senderLabel(message: MailboxMessageSummary): string {
    if (!message.from) return '(sin remitente)';
    return message.from.name?.trim() || message.from.address;
  }

  /// Hoy solo la hora, y para el resto el día y el mes: es lo que hace cualquier cliente de correo y ahorra
  /// espacio en un panel estrecho.
  dateLabel(date: Date): string {
    const value = date instanceof Date ? date : new Date(date);
    const today = new Date();
    const sameDay =
      value.getDate() === today.getDate() &&
      value.getMonth() === today.getMonth() &&
      value.getFullYear() === today.getFullYear();

    if (sameDay) return value.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    if (value.getFullYear() === today.getFullYear()) {
      return value.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
    }
    return value.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
  }
}
